import { useState, useMemo } from 'react';
import { attendanceAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { Download, Filter, Clock, CheckCircle, Edit, Plus, Loader2, FileSpreadsheet, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useAttendanceData } from '../hooks/useAttendanceData';
import { DailyDetailTable } from '../components/attendance/DailyDetailTable';
import { MonthlySummaryTable } from '../components/attendance/MonthlySummaryTable';
import { ExceptionsTab } from '../components/attendance/ExceptionsTab';

export default function AttendanceManagement() {
  const {
    loading, monthlyLoading, dailySummary, monthlySummary, exceptions, employees,
    activeTab, setActiveTab,
    selectedMonth, setSelectedMonth,
    selectedYear, setSelectedYear,
    selectedEmployee, setSelectedEmployee,
    showAllEmployees, setShowAllEmployees,
    payCycleFilter, setPayCycleFilter,
    payPeriods,
    selectedPayPeriod, setSelectedPayPeriod,
    dateFrom, setDateFrom,
    dateTo, setDateTo,
    exceptionStatus, setExceptionStatus,
    fetchData,
  } = useAttendanceData();

  // Correction modal
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionData, setCorrectionData] = useState({
    employee_id: '',
    date: '',
    clock_in: '',
    clock_out: '',
    clock_out_date: '',
    shift_type: 'Day Shift',
    notes: ''
  });
  const [savingCorrection, setSavingCorrection] = useState(false);

  // Exception management (Feb 2026)
  const [selectedExceptionKeys, setSelectedExceptionKeys] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({
    employee_id: '', employee_name: '', shift_date: '', leave_type: 'Annual Leave', notes: '',
  });
  const [convertSaving, setConvertSaving] = useState(false);

  const handleExportExcel = async () => {
    try {
      const hourlyOnly = !showAllEmployees;
      const cycleType = payCycleFilter === 'all' ? null : payCycleFilter;
      const usingCustomRange = Boolean(dateFrom || dateTo);
      let periodStart = null;
      let periodEnd = null;
      if (usingCustomRange) {
        periodStart = dateFrom || dateTo;
        periodEnd = dateTo || dateFrom;
      } else if (selectedPayPeriod !== 'all') {
        const periodObj = payPeriods.find(p => p.period_start === selectedPayPeriod);
        periodStart = periodObj?.period_start || null;
        periodEnd = periodObj?.period_end || null;
      }
      const monthParam = usingCustomRange ? null : selectedMonth;
      const yearParam = usingCustomRange ? null : selectedYear;
      const response = await attendanceAPI.exportExcel(monthParam, yearParam, hourlyOnly, cycleType, periodStart, periodEnd);
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cycleSuffix = cycleType ? `_${cycleType}` : '';
      const rangeSuffix = usingCustomRange ? `_${periodStart}_to_${periodEnd}` : `_${selectedYear}_${String(selectedMonth).padStart(2, '0')}`;
      a.download = `attendance${rangeSuffix}${cycleSuffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Excel report downloaded');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  // ---- Exception bulk actions (Feb 2026) ----
  const exceptionKey = (r) => `${r.employee_id}__${r.shift_date || r.date}`;
  const toggleExceptionSelected = (r) => {
    const k = exceptionKey(r);
    const next = new Set(selectedExceptionKeys);
    next.has(k) ? next.delete(k) : next.add(k);
    setSelectedExceptionKeys(next);
  };
  const toggleAllExceptions = () => {
    if (selectedExceptionKeys.size === exceptions.length) {
      setSelectedExceptionKeys(new Set());
    } else {
      setSelectedExceptionKeys(new Set(exceptions.map(exceptionKey)));
    }
  };
  const handleBulkExceptionAction = async (action) => {
    if (selectedExceptionKeys.size === 0) {
      toast.error('Select at least one exception first');
      return;
    }
    if (selectedExceptionKeys.size > 10 && !window.confirm(`Apply '${action}' to ${selectedExceptionKeys.size} exceptions? This will affect the active queue.`)) {
      return;
    }
    setBulkActionLoading(true);
    try {
      const items = Array.from(selectedExceptionKeys).map((k) => {
        const [employee_id, shift_date] = k.split('__');
        return { employee_id, shift_date };
      });
      const res = await attendanceAPI.exceptionsAction({ action, items });
      toast.success(`Updated ${res.data.updated} exception${res.data.updated === 1 ? '' : 's'}`);
      setSelectedExceptionKeys(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkActionLoading(false);
    }
  };
  const openConvertModal = (record) => {
    setConvertForm({
      employee_id: record.employee_id,
      employee_name: record.employee_name,
      shift_date: record.shift_date || record.date,
      leave_type: 'Annual Leave',
      notes: '',
    });
    setConvertModalOpen(true);
  };
  const handleConvertSubmit = async () => {
    if (!convertForm.leave_type) { toast.error('Pick a leave type'); return; }
    setConvertSaving(true);
    try {
      await attendanceAPI.exceptionsConvertToLeave({
        employee_id: convertForm.employee_id,
        shift_date: convertForm.shift_date,
        leave_type: convertForm.leave_type,
        notes: convertForm.notes,
      });
      toast.success('Pending leave request created — visible on Leave page for approval');
      setConvertModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert exception');
    } finally {
      setConvertSaving(false);
    }
  };


  const handleOpenCorrection = (record = null) => {
    if (record) {
      setCorrectionData({
        employee_id: record.employee_id,
        date: record.shift_date || record.date,
        clock_in: record.first_clock_in?.slice(0, 5) || '',
        clock_out: record.last_clock_out?.slice(0, 5) || '',
        clock_out_date: record.last_clock_out_date || record.shift_date || record.date,
        shift_type: record.shift_type || 'Day Shift',
        notes: ''
      });
    } else {
      setCorrectionData({
        employee_id: '',
        date: new Date().toISOString().split('T')[0],
        clock_in: '',
        clock_out: '',
        clock_out_date: '',
        shift_type: 'Day Shift',
        notes: ''
      });
    }
    setShowCorrectionModal(true);
  };

  const handleSaveCorrection = async () => {
    if (!correctionData.employee_id || !correctionData.date) {
      toast.error('Employee and date are required');
      return;
    }
    
    setSavingCorrection(true);
    try {
      await attendanceAPI.correct(correctionData);
      toast.success('Attendance record corrected');
      setShowCorrectionModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save correction');
    } finally {
      setSavingCorrection(false);
    }
  };

  // Filter data by selected employee (in addition to backend filter)
  const filteredDaily = selectedEmployee === 'all'
    ? dailySummary
    : dailySummary.filter(d => d.employee_id === selectedEmployee);

  const filteredMonthly = selectedEmployee === 'all'
    ? monthlySummary
    : monthlySummary.filter(d => d.employee_id === selectedEmployee);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [2024, 2025, 2026, 2027];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Time & Attendance</h1>
          <p className="text-muted-foreground">Manage employee attendance records</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => handleOpenCorrection()} data-testid="add-correction-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </Button>
          <Button onClick={handleExportExcel} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="space-y-2">
            <Label>Month</Label>
            <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
              <SelectTrigger className="w-40" data-testid="filter-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(m => (
                  <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Year</Label>
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-32" data-testid="filter-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Employee</Label>
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="w-56" data-testid="filter-employee">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map(e => (
                  <SelectItem key={e.employee_id} value={e.employee_id}>
                    {e.full_name} {e.is_hourly_paid ? '' : '(non-hourly)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Pay Cycle</Label>
            <Select value={payCycleFilter} onValueChange={setPayCycleFilter}>
              <SelectTrigger className="w-40" data-testid="filter-pay-cycle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cycles</SelectItem>
                <SelectItem value="Fortnightly">Fortnightly</SelectItem>
                <SelectItem value="Monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {payCycleFilter !== 'all' && payPeriods.length > 0 && (
            <div className="space-y-2">
              <Label>Pay Period</Label>
              <Select value={selectedPayPeriod} onValueChange={setSelectedPayPeriod}>
                <SelectTrigger className="w-64" data-testid="filter-pay-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Periods ({selectedYear})</SelectItem>
                  {payPeriods.map(p => (
                    <SelectItem key={p.period_start} value={p.period_start}>
                      {p.period_start} → {p.period_end}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Custom date-range filter — overrides month/year + pay period when set */}
          <div className="space-y-2">
            <Label>Date From</Label>
            <Input
              type="date"
              value={dateFrom}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => {
                const v = e.target.value;
                const today = new Date().toISOString().slice(0, 10);
                if (v && v > today) {
                  toast.error('Future dates cannot be selected');
                  return;
                }
                setDateFrom(v);
              }}
              className="w-44"
              data-testid="filter-date-from"
            />
          </div>
          <div className="space-y-2">
            <Label>Date To</Label>
            <Input
              type="date"
              value={dateTo}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => {
                const v = e.target.value;
                const today = new Date().toISOString().slice(0, 10);
                if (v && v > today) {
                  toast.error('Future dates cannot be selected');
                  return;
                }
                setDateTo(v);
              }}
              className="w-44"
              data-testid="filter-date-to"
            />
          </div>
          <div className="space-y-2">
            <Label className="opacity-0 select-none">.</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  setDateFrom(today);
                  setDateTo(today);
                }}
                data-testid="filter-today-btn"
              >
                Today
              </Button>
              {(dateFrom || dateTo) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 text-muted-foreground"
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  data-testid="filter-clear-date-btn"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {/* Show All Employees Toggle */}
          <div className="flex items-center space-x-3 ml-auto p-3 rounded-lg bg-secondary/30">
            <Switch
              id="show-all"
              checked={showAllEmployees}
              onCheckedChange={setShowAllEmployees}
              data-testid="toggle-show-all"
            />
            <Label htmlFor="show-all" className="text-sm cursor-pointer flex items-center gap-2">
              {showAllEmployees ? (
                <>
                  <Eye className="w-4 h-4" />
                  Showing All Employees
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4" />
                  Hourly Employees Only
                </>
              )}
            </Label>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-secondary/30 w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="daily">Daily Detail</TabsTrigger>
          <TabsTrigger value="monthly">Monthly Summary</TabsTrigger>
          <TabsTrigger value="exceptions">
            Exceptions
            {exceptions.length > 0 && (
              <Badge variant="destructive" className="ml-2">{exceptions.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Daily Detail Tab */}
        <TabsContent value="daily">
          <DailyDetailTable loading={loading} records={filteredDaily} onEdit={handleOpenCorrection} />
        </TabsContent>

        {/* Monthly Summary Tab */}
        <TabsContent value="monthly">
          <MonthlySummaryTable loading={monthlyLoading} rows={filteredMonthly} />
        </TabsContent>

        {/* Exceptions Tab */}
        <TabsContent value="exceptions" className="space-y-4">
          <ExceptionsTab
            loading={loading}
            exceptions={exceptions}
            exceptionStatus={exceptionStatus}
            setExceptionStatus={setExceptionStatus}
            selectedExceptionKeys={selectedExceptionKeys}
            setSelectedExceptionKeys={setSelectedExceptionKeys}
            bulkActionLoading={bulkActionLoading}
            handleBulkExceptionAction={handleBulkExceptionAction}
            toggleAllExceptions={toggleAllExceptions}
            toggleExceptionSelected={toggleExceptionSelected}
            exceptionKey={exceptionKey}
            handleOpenCorrection={handleOpenCorrection}
            openConvertModal={openConvertModal}
          />
        </TabsContent>
      </Tabs>

      {/* Correction Modal */}
      <Dialog open={showCorrectionModal} onOpenChange={setShowCorrectionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Correct Attendance Record</DialogTitle>
            <DialogDescription>
              Add or correct clock-in and clock-out times
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select 
                value={correctionData.employee_id} 
                onValueChange={(v) => {
                  const emp = employees.find(e => e.employee_id === v);
                  setCorrectionData({
                    ...correctionData,
                    employee_id: v,
                    shift_type: emp?.shift_type || 'Day Shift',
                  });
                }}
              >
                <SelectTrigger data-testid="correction-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.full_name} {emp.shift_type === 'Night Shift' ? '(Night)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={correctionData.date}
                onChange={(e) => setCorrectionData({...correctionData, date: e.target.value})}
                data-testid="correction-date"
              />
              <p className="text-xs text-muted-foreground">
                This is the <strong>Shift Date</strong> — the day the shift started (for Night Shift, the evening clock-in date).
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Clock In</Label>
                <Input
                  type="time"
                  value={correctionData.clock_in}
                  onChange={(e) => setCorrectionData({...correctionData, clock_in: e.target.value})}
                  data-testid="correction-clock-in"
                />
              </div>
              <div className="space-y-2">
                <Label>Clock Out</Label>
                <Input
                  type="time"
                  value={correctionData.clock_out}
                  onChange={(e) => setCorrectionData({...correctionData, clock_out: e.target.value})}
                  data-testid="correction-clock-out"
                />
              </div>
            </div>

            {correctionData.shift_type === 'Night Shift' && (
              <div className="space-y-2">
                <Label>Clock Out Date (next morning)</Label>
                <Input
                  type="date"
                  value={correctionData.clock_out_date || correctionData.date}
                  onChange={(e) => setCorrectionData({...correctionData, clock_out_date: e.target.value})}
                  data-testid="correction-clock-out-date"
                />
                <p className="text-xs text-muted-foreground">
                  For an overnight shift, set this to the <strong>next day</strong> after Shift Date.
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={correctionData.notes}
                onChange={(e) => setCorrectionData({...correctionData, notes: e.target.value})}
                placeholder="Reason for correction..."
                data-testid="correction-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectionModal(false)}>Cancel</Button>
            <Button onClick={handleSaveCorrection} disabled={savingCorrection} data-testid="save-correction-btn">
              {savingCorrection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Exception to Leave Modal */}
      <Dialog open={convertModalOpen} onOpenChange={setConvertModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Convert Exception to Leave</DialogTitle>
            <DialogDescription>
              Create a Pending leave request for <strong>{convertForm.employee_name}</strong> on <strong>{convertForm.shift_date}</strong>.
              HR will still need to approve it from the Leave page. The exception will be marked Resolved.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Leave Type *</Label>
              <Select
                value={convertForm.leave_type}
                onValueChange={(v) => setConvertForm({ ...convertForm, leave_type: v })}
              >
                <SelectTrigger data-testid="convert-leave-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Annual Leave">Annual Leave (paid)</SelectItem>
                  <SelectItem value="Sick Leave">Sick Leave (paid)</SelectItem>
                  <SelectItem value="Family Responsibility Leave">Family Responsibility Leave (paid)</SelectItem>
                  <SelectItem value="Public Holiday Leave">Public Holiday Leave (paid)</SelectItem>
                  <SelectItem value="Unpaid Leave">Unpaid Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                rows={3}
                value={convertForm.notes}
                onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })}
                placeholder="Doctor letter on file / Phoned in sick / etc."
                data-testid="convert-leave-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertModalOpen(false)} disabled={convertSaving}>Cancel</Button>
            <Button onClick={handleConvertSubmit} disabled={convertSaving} data-testid="convert-leave-submit">
              {convertSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Pending Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
